import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Formik } from 'formik'
import * as yup from 'yup'
import { AiOutlineEyeInvisible, AiOutlineEye } from 'react-icons/ai'
import { useTranslation } from 'react-i18next'
import { countries, countriesGe } from './Data'

import './Register.css'

import { Row, Col, Form, Button, InputGroup, Container, Alert, Spinner } from 'react-bootstrap'
import { shallowEqual, useSelector } from 'react-redux'
import { $api } from '../../helpers/api'

const Register = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const [show, setShow] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [serverError, setServerError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const user = useSelector(state => state?.user?.data, shallowEqual)

  useEffect(() => {
    if (user?.id) {
      history.goBack()
    }
  }, [user, history])

  // Clear messages after 5 seconds
  useEffect(() => {
    if (serverError || successMessage) {
      const timer = setTimeout(() => {
        setServerError('')
        setSuccessMessage('')
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [serverError, successMessage])

  const userHelper = () => {
    return (
      <div>
        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
          <li>{t('Registration.error1')}</li>
          <li>{t('Registration.error2')}</li>
          {t('Registration.error3') !== '' ? <li>{t('Registration.error3')}</li> : null}
        </ul>
      </div>
    )
  }

  // Improved regex patterns
  const USERNAME_REGEX = /^[a-z0-9_]{3,}[a-z]+[0-9]*$/
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const PHONE_REGEX = /^[\+]?[1-9][\d]{0,15}$/

  // Enhanced validation schema
  const schema = yup.object().shape({
    username: yup
      .string()
      .min(3, t('Registration.usernameMinLength') || 'Username must be at least 3 characters')
      .max(20, t('Registration.usernameMaxLength') || 'Username cannot exceed 20 characters')
      .matches(USERNAME_REGEX, {
        message: userHelper,
        excludeEmptyString: true,
      })
      .required(t('Registration.requiredfield') || 'This field is required'),

    userfullname: yup
      .string()
      .min(2, t('Registration.fullnameMinLength') || 'Full name must be at least 2 characters')
      .max(50, t('Registration.fullnameMaxLength') || 'Full name cannot exceed 50 characters')
      .matches(
        /^[a-zA-Zა-ჰ\s]+$/,
        t('Registration.fullnameInvalid') || 'Full name can only contain letters and spaces',
      )
      .required(t('Registration.requiredfield') || 'This field is required'),

    email: yup
      .string()
      .email(t('Registration.emailInvalid') || 'Please enter a valid email address')
      .min(4, t('Registration.emailMinLength') || 'Email must be at least 4 characters')
      .max(100, t('Registration.emailMaxLength') || 'Email cannot exceed 100 characters')
      .matches(EMAIL_REGEX, t('Registration.erroremail') || 'Please enter a valid email address')
      .required(t('Registration.requiredfield') || 'This field is required'),

    password: yup
      .string()
      .min(6, t('Registration.passwordMinLength') || 'Password must be at least 6 characters')
      .max(100, t('Registration.passwordMaxLength') || 'Password cannot exceed 100 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        t('Registration.passwordComplexity') ||
          'Password must contain at least one uppercase letter, one lowercase letter, and one number',
      )
      .required(t('Registration.requiredfield') || 'This field is required'),

    country: yup.string().required(t('Registration.requiredfield') || 'This field is required'),

    address: yup
      .string()
      .min(5, t('Registration.addressMinLength') || 'Address must be at least 5 characters')
      .max(200, t('Registration.addressMaxLength') || 'Address cannot exceed 200 characters')
      .required(t('Registration.requiredfield') || 'This field is required'),

    phonenumber: yup
      .string()
      .matches(PHONE_REGEX, t('Registration.phoneInvalid') || 'Please enter a valid phone number')
      .required(t('Registration.requiredfield') || 'This field is required'),

    birthdate: yup
      .date()
      .max(
        new Date(Date.now() - 567648000000),
        t('Registration.ageRestriction') || 'You must be at least 18 years old',
      ) // 18 years ago
      .min(
        new Date(Date.now() - 3155760000000),
        t('Registration.ageLimit') || 'Please enter a valid birth date',
      ) // 100 years ago
      .required(t('Registration.requiredfield') || 'This field is required'),

    passwordConfirmation: yup
      .string()
      .oneOf([yup.ref('password'), null], t('Registration.errorpassword') || 'Passwords must match')
      .required(t('Registration.requiredfield') || 'This field is required'),
  })

  // Error display component
  const ErrorAlert = () => {
    if (!serverError) return null

    return (
      <Alert variant='danger' className='mb-3' dismissible onClose={() => setServerError('')}>
        <Alert.Heading className='h6'>
          {t('Registration.errorTitle') || 'Registration Error'}
        </Alert.Heading>
        <p className='mb-0'>{serverError}</p>
      </Alert>
    )
  }

  // Success message component
  const SuccessAlert = () => {
    if (!successMessage) return null

    return (
      <Alert variant='success' className='mb-3' dismissible onClose={() => setSuccessMessage('')}>
        <Alert.Heading className='h6'>
          {t('Registration.successTitle') || 'Registration Successful'}
        </Alert.Heading>
        <p className='mb-0'>{successMessage}</p>
      </Alert>
    )
  }

  // Handle form submission
  const handleSubmit = async (values, { setFieldError, setSubmitting, resetForm }) => {
    try {
      setIsLoading(true)
      setServerError('')
      setSuccessMessage('')

      // Sanitize form data
      const sanitizedValues = {
        ...values,
        username: values.username.toLowerCase().trim(),
        email: values.email.toLowerCase().trim(),
        userfullname: values.userfullname.trim(),
        address: values.address.trim(),
        phonenumber: values.phonenumber.trim(),
        country: values.country.trim(),
      }

      const response = await $api.post('/users', sanitizedValues)

      if (response.data.error) {
        // Handle specific field errors
        if (response.data.field) {
          setFieldError(response.data.field, response.data.error)
        } else {
          setServerError(response.data.error)
        }
      } else {
        setSuccessMessage(
          t('Registration.successMessage') ||
            'Registration successful! You will be redirected to login page.',
        )

        // Reset form and redirect after success
        resetForm()
        setTimeout(() => {
          history.push('/login')
        }, 2000)
      }
    } catch (error) {
      console.error('Registration error:', error)

      // Handle different error types
      let errorMessage = t('Registration.networkError') || 'Network error. Please try again.'

      if (error.response) {
        switch (error.response.status) {
          case 409:
            errorMessage = t('Registration.userExists') || 'Username or email already exists'
            break
          case 400:
            errorMessage = t('Registration.validationError') || 'Please check your input data'
            break
          case 500:
            errorMessage = t('Registration.serverError') || 'Server error. Please try again later'
            break
          default:
            errorMessage = error.response.data?.message || errorMessage
        }
      }

      setServerError(errorMessage)
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  // Password visibility type
  const pswdType = show ? 'text' : 'password'

  return (
    <Container>
      <Formik
        initialValues={{
          username: '',
          userfullname: '',
          birthdate: '',
          country: t('Registration.initialCountry') || '',
          address: '',
          phonenumber: '',
          email: '',
          password: '',
          passwordConfirmation: '',
          role: 'User',
        }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, handleChange, values, touched, errors, isSubmitting, isValid }) => (
          <div className='register-page'>
            <Row className='justify-content-md-center register-row'>
              <h4>{t('Registration.register')}</h4>

              <Col md='5' className='register-form'>
                <ErrorAlert />
                <SuccessAlert />

                <Form noValidate onSubmit={handleSubmit}>
                  {/* Username Field */}
                  <Form.Group className='mb-3' controlId='validationFormikUsername'>
                    <Form.Label>{t('Registration.user')}</Form.Label>
                    <InputGroup hasValidation>
                      <Form.Control
                        type='text'
                        placeholder={t('Registration.username')}
                        aria-describedby='inputGroupPrepend'
                        name='username'
                        value={values.username}
                        onChange={handleChange}
                        isInvalid={touched.username && !!errors.username}
                        isValid={touched.username && !errors.username}
                        disabled={isLoading}
                        autoComplete='username'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {typeof errors.username === 'function'
                          ? errors.username()
                          : errors.username}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Full Name Field */}
                  <Form.Group className='mb-3' controlId='validationFormikFullname'>
                    <Form.Label>{t('Registration.fullname')}</Form.Label>
                    <Form.Control
                      type='text'
                      name='userfullname'
                      placeholder={t('Registration.fullname')}
                      value={values.userfullname}
                      onChange={handleChange}
                      isInvalid={touched.userfullname && !!errors.userfullname}
                      isValid={touched.userfullname && !errors.userfullname}
                      disabled={isLoading}
                      autoComplete='name'
                    />
                    <Form.Control.Feedback type='invalid'>
                      {errors.userfullname}
                    </Form.Control.Feedback>
                    <Form.Control.Feedback>
                      {t('Registration.looksGood') || 'Looks good!'}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Birth Date Field */}
                  <Form.Group className='mb-3' controlId='validationFormikBirthdate'>
                    <Form.Label>{t('Registration.birthdate')}</Form.Label>
                    <InputGroup hasValidation>
                      <Form.Control
                        type='date'
                        placeholder={t('Registration.birthdate')}
                        aria-describedby='inputGroupPrepend'
                        name='birthdate'
                        value={values.birthdate}
                        onChange={handleChange}
                        isInvalid={touched.birthdate && !!errors.birthdate}
                        isValid={touched.birthdate && !errors.birthdate}
                        disabled={isLoading}
                        max={new Date(Date.now() - 567648000000).toISOString().split('T')[0]} // 18 years ago
                        min={new Date(Date.now() - 3155760000000).toISOString().split('T')[0]} // 100 years ago
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.birthdate}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Country Field */}
                  <Form.Group className='mb-3' controlId='validationFormikCountry2'>
                    <Form.Label>{t('Registration.country')}</Form.Label>
                    <Form.Control
                      as='select'
                      name='country'
                      value={values.country}
                      onChange={handleChange}
                      isInvalid={touched.country && !!errors.country}
                      isValid={touched.country && !errors.country}
                      disabled={isLoading}
                    >
                      <option value=''>
                        {t('Registration.selectCountry') || 'Select a country'}
                      </option>
                      {t('Registration.language') !== 'ka' ? (
                        <>
                          {countries.map(country => (
                            <option key={country.code} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                        </>
                      ) : (
                        <>
                          {countriesGe.map(country => (
                            <option key={country.code} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                        </>
                      )}
                    </Form.Control>
                    <Form.Control.Feedback type='invalid'>{errors.country}</Form.Control.Feedback>
                  </Form.Group>

                  {/* Address Field */}
                  <Form.Group className='mb-3' controlId='validationFormikAddress'>
                    <Form.Label>{t('Registration.address')}</Form.Label>
                    <InputGroup hasValidation>
                      <Form.Control
                        type='text'
                        placeholder={t('Registration.address')}
                        aria-describedby='inputGroupPrepend'
                        name='address'
                        value={values.address}
                        onChange={handleChange}
                        isInvalid={touched.address && !!errors.address}
                        isValid={touched.address && !errors.address}
                        disabled={isLoading}
                        autoComplete='address-line1'
                      />
                      <Form.Control.Feedback type='invalid'>{errors.address}</Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Phone Number Field */}
                  <Form.Group className='mb-3' controlId='validationFormikPhonenumber'>
                    <Form.Label>{t('Registration.phonenumber')}</Form.Label>
                    <InputGroup hasValidation>
                      <Form.Control
                        type='tel'
                        placeholder={t('Registration.phonenumber')}
                        aria-describedby='inputGroupPrepend'
                        name='phonenumber'
                        value={values.phonenumber}
                        onChange={handleChange}
                        isInvalid={touched.phonenumber && !!errors.phonenumber}
                        isValid={touched.phonenumber && !errors.phonenumber}
                        disabled={isLoading}
                        autoComplete='tel'
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.phonenumber}
                      </Form.Control.Feedback>
                    </InputGroup>
                  </Form.Group>

                  {/* Email Field */}
                  <Form.Group className='mb-3' controlId='validationFormikEmail'>
                    <Form.Label>{t('Registration.email')}</Form.Label>
                    <Form.Control
                      type='email'
                      name='email'
                      placeholder={t('Registration.email')}
                      value={values.email}
                      onChange={handleChange}
                      isInvalid={touched.email && !!errors.email}
                      isValid={touched.email && !errors.email}
                      disabled={isLoading}
                      autoComplete='email'
                    />
                    <Form.Control.Feedback type='invalid'>{errors.email}</Form.Control.Feedback>
                    <Form.Control.Feedback>
                      {t('Registration.looksGood') || 'Looks good!'}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Password Field */}
                  <Form.Group className='mb-3' controlId='validationFormikPassword'>
                    <Form.Label>{t('Registration.password')}</Form.Label>
                    <div className='password'>
                      <Form.Control
                        type={pswdType}
                        placeholder={t('Registration.password')}
                        name='password'
                        value={values.password}
                        onChange={handleChange}
                        isInvalid={touched.password && !!errors.password}
                        isValid={touched.password && !errors.password}
                        disabled={isLoading}
                        autoComplete='new-password'
                      />
                      <InputGroup.Text
                        onClick={() => setShow(!show)}
                        id='inputGroupPrepend'
                        style={{ cursor: 'pointer' }}
                        tabIndex={0}
                        role='button'
                        aria-label={show ? 'Hide password' : 'Show password'}
                      >
                        {show ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                      </InputGroup.Text>
                    </div>
                    <Form.Control.Feedback type='invalid'>{errors.password}</Form.Control.Feedback>
                  </Form.Group>

                  {/* Password Confirmation Field */}
                  <Form.Group className='mb-3' controlId='validationFormikRepassword'>
                    <Form.Label>{t('Registration.repassword')}</Form.Label>
                    <div className='password'>
                      <Form.Control
                        required
                        type={pswdType}
                        placeholder={t('Registration.repassword')}
                        name='passwordConfirmation'
                        value={values.passwordConfirmation}
                        onChange={handleChange}
                        isInvalid={touched.passwordConfirmation && !!errors.passwordConfirmation}
                        isValid={touched.passwordConfirmation && !errors.passwordConfirmation}
                        disabled={isLoading}
                        autoComplete='new-password'
                      />
                      <InputGroup.Text
                        onClick={() => setShow(!show)}
                        id='inputGroupPrepend'
                        style={{ cursor: 'pointer' }}
                        tabIndex={0}
                        role='button'
                        aria-label={show ? 'Hide password' : 'Show password'}
                      >
                        {show ? <AiOutlineEye /> : <AiOutlineEyeInvisible />}
                      </InputGroup.Text>
                    </div>
                    <Form.Control.Feedback type='invalid'>
                      {errors.passwordConfirmation}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Submit Button */}
                  <Button
                    type='submit'
                    disabled={isLoading || isSubmitting || !isValid}
                    className='w-100'
                    size='lg'
                  >
                    {isLoading || isSubmitting ? (
                      <>
                        <Spinner
                          as='span'
                          animation='border'
                          size='sm'
                          role='status'
                          aria-hidden='true'
                          className='me-2'
                        />
                        {t('Registration.registering') || 'Registering...'}
                      </>
                    ) : (
                      t('Registration.registrationbutton')
                    )}
                  </Button>
                </Form>
              </Col>
            </Row>
          </div>
        )}
      </Formik>
    </Container>
  )
}

export default Register
